'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PopupData = {
  id: string;
  title?: string | null;
  content?: string | null;
  type: 'NOTICE' | 'EVENT';
  imageUrl?: string | null;
  linkUrl?: string | null;
  order: number;
};

interface IntroPopupProps {
  popups: PopupData[];
  onClose: () => void;
}

export default function IntroPopup({ popups, onClose }: IntroPopupProps) {
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  
  const currentPopup = popups[currentPopupIndex];
  
  const handleNext = () => {
    if (currentPopupIndex < popups.length - 1) {
      setCurrentPopupIndex(currentPopupIndex + 1);
    } else {
      handleClose();
    }
  };
  
  const handleClose = () => {
    setVisible(false);
    // Add small delay before calling onClose to allow exit animation
    setTimeout(() => {
      onClose();
    }, 200);
  };

  if (!visible || !currentPopup) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div 
        className={cn(
          "relative bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 animate-fade-in",
          currentPopup.type === 'EVENT' && !currentPopup.title ? 'p-0' : 'p-6'
        )}
      >
        <button 
          onClick={handleClose}
          className="absolute top-2 right-2 bg-white/80 rounded-full p-1 z-10 hover:bg-gray-100 transition-colors"
          aria-label="닫기"
        >
          <X className="h-5 w-5" />
        </button>
        
        {currentPopup.type === 'EVENT' && currentPopup.imageUrl && !currentPopup.title ? (
          // Banner style popup (just image, no title)
          <div className="relative">
            {currentPopup.linkUrl ? (
              <Link href={currentPopup.linkUrl} className="block w-full">
                <Image 
                  src={currentPopup.imageUrl} 
                  alt={currentPopup.content || "이벤트 배너"} 
                  width={1000} 
                  height={600} 
                  className="rounded-lg"
                  style={{ width: 'auto', height: 'auto' }}
                  priority
                />
              </Link>
            ) : (
              <Image 
                src={currentPopup.imageUrl} 
                alt={currentPopup.content || "이벤트 배너"} 
                width={1000} 
                height={600} 
                className="rounded-lg"
                style={{ width: 'auto', height: 'auto' }}
                priority
              />
            )}
          </div>
        ) : (
          // Regular popup with title and content
          <>
            {currentPopup.title && (
              <h2 className="text-xl font-bold mb-4">{currentPopup.title}</h2>
            )}
            
            <div className="space-y-4">
              {currentPopup.imageUrl && (
                <div className="relative">
                  <Image 
                    src={currentPopup.imageUrl} 
                    alt="" 
                    width={1000} 
                    height={600} 
                    className="rounded-lg"
                    style={{ width: 'auto', height: 'auto' }}
                    priority
                  />
                </div>
              )}
              
              {currentPopup.content && (
                <div className="whitespace-pre-wrap">{currentPopup.content}</div>
              )}
              
              {currentPopup.linkUrl && (
                <div className="mt-4">
                  <Link 
                    href={currentPopup.linkUrl}
                    className="inline-block bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 transition-colors"
                  >
                    자세히 보기
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
        
        {popups.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/30 px-3 py-1 rounded text-white text-sm">
            {currentPopupIndex + 1}/{popups.length}
            
            {currentPopupIndex < popups.length - 1 && (
              <button
                onClick={handleNext}
                className="ml-3 text-sm text-white underline"
              >
                다음
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
